// Copyright (c) 2026 Watch Tower Bible and Tract Society of Pennsylvania
// All Rights Reserved. Proprietary and confidential.
// Unauthorized copying, modification, or distribution is strictly prohibited.
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { useCapacitor } from "./hooks/useCapacitor";
import { PWAInstallPrompt } from "./components/PWAInstallPrompt";
import UpdateNotifier from "./components/UpdateNotifier";
import WhatsNewDialog from "./components/WhatsNewDialog";
import LoginPage from "./pages/LoginPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import SelectAreaPage from "./pages/SelectAreaPage";
import DashboardPage from "./pages/DashboardPage";
import DocumentsPage from "./pages/DocumentsPage";
import AdminUsersPage from "./pages/AdminUsersPage";
import AuditLogsPage from "./pages/AuditLogsPage";
import AuditFormPage from "./pages/AuditFormPage";
import AuditFormEditorPage from "./pages/AuditFormEditorPage";
import AssistantPage from "./pages/AssistantPage";
import AuditChecklistPage from "./pages/AuditChecklistPage";
import PermanentApprovalsPage from "./pages/PermanentApprovalsPage";
import DiscrepanciesPage from "./pages/DiscrepanciesPage";
import MaintenanceForecastPage from "./pages/MaintenanceForecastPage";
import MyAccountPage from "./pages/MyAccountPage";

// Capacitor initialization component
function CapacitorInit({ children }) {
  useCapacitor();
  return children;
}

// Protected Route Component
function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-8 h-8 border-4 border-muted border-t-primary rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/" replace />;
  }

  return children;
}

// Admin Protected Route
function AdminRoute({ children }) {
  const { user, loading, isAdmin } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-8 h-8 border-4 border-muted border-t-primary rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/" replace />;
  }

  if (!isAdmin()) {
    return <Navigate to="/select-area" replace />;
  }

  return children;
}

// Area Protected Route - requires area selection and permission
function AreaProtectedRoute({ children }) {
  const { user, loading, hasPermission } = useAuth();
  const selectedArea = localStorage.getItem('selectedArea');

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-8 h-8 border-4 border-muted border-t-primary rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/" replace />;
  }

  if (!selectedArea) {
    return <Navigate to="/select-area" replace />;
  }

  // Check if user has permission for the selected area
  if (!hasPermission(selectedArea)) {
    localStorage.removeItem('selectedArea');
    return <Navigate to="/select-area" replace />;
  }

  return children;
}

// Public Route Component (redirects to select-area if logged in)
function PublicRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-8 h-8 border-4 border-muted border-t-primary rounded-full animate-spin"></div>
      </div>
    );
  }

  if (user) {
    if (user.must_change_password) {
      return <Navigate to="/minha-conta" replace />;
    }
    const selectedArea = localStorage.getItem('selectedArea');
    if (selectedArea) {
      return <Navigate to="/dashboard" replace />;
    }
    return <Navigate to="/select-area" replace />;
  }

  return children;
}

function AppRoutes() {
  return (
    <Routes>
      <Route
        path="/"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />
      <Route
        path="/forgot-password"
        element={
          <PublicRoute>
            <ForgotPasswordPage />
          </PublicRoute>
        }
      />
      <Route
        path="/reset-password"
        element={<ResetPasswordPage />}
      />
      <Route
        path="/select-area"
        element={
          <ProtectedRoute>
            <SelectAreaPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/minha-conta"
        element={
          <ProtectedRoute>
            <MyAccountPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard"
        element={
          <AreaProtectedRoute>
            <DashboardPage />
          </AreaProtectedRoute>
        }
      />
      <Route
        path="/documents"
        element={
          <AreaProtectedRoute>
            <DocumentsPage />
          </AreaProtectedRoute>
        }
      />
      <Route
        path="/audit-form"
        element={
          <AreaProtectedRoute>
            <AuditFormPage />
          </AreaProtectedRoute>
        }
      />
      <Route
        path="/admin/users"
        element={
          <AdminRoute>
            <AdminUsersPage />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/audit-logs"
        element={
          <AdminRoute>
            <AuditLogsPage />
          </AdminRoute>
        }
      />
      <Route
        path="/audit-form/edit/:formId"
        element={
          <ProtectedRoute>
            <AuditFormEditorPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/assistente"
        element={
          <ProtectedRoute>
            <AssistantPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/auditoria-trimestral"
        element={
          <AreaProtectedRoute>
            <AuditChecklistPage />
          </AreaProtectedRoute>
        }
      />
      <Route
        path="/aprovacoes-permanentes"
        element={
          <AreaProtectedRoute>
            <PermanentApprovalsPage />
          </AreaProtectedRoute>
        }
      />
      <Route
        path="/discrepancias"
        element={
          <AreaProtectedRoute>
            <DiscrepanciesPage />
          </AreaProtectedRoute>
        }
      />
      <Route
        path="/previsao-gastos"
        element={
          <AreaProtectedRoute>
            <MaintenanceForecastPage />
          </AreaProtectedRoute>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <CapacitorInit>
            <AppRoutes />
            <PWAInstallPrompt />
            <UpdateNotifier />
            <WhatsNewDialog />
            <Toaster position="top-right" richColors />
          </CapacitorInit>
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
